import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import useOnClickOutside, {
  preventPropagationProps,
} from '../../../hooks/useOnClickOutside';
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';

import BURGER_ICON_BLUE from '../../../images/ic-menu-blue.svg';
import BURGER_ICON_WHITE from '../../../images/ic-menu-white.svg';

import BURGER_CLOSE_ICON_BLUE from '../../../images/ic-menu-close-blue.svg';
import BURGER_CLOSE_ICON_WHITE from '../../../images/ic-menu-close-white.svg';

import {
  HEADER_BURGER_MENU_BUTTON,
  HEADER_BURGER_MENU_COLLAPSE_BUTTON,
} from '../../../../automationHooks.json';

const propTypes = {
  headerHeight: PropTypes.number,
  isAuthenticated: PropTypes.bool,
  children: PropTypes.node.isRequired,
  isStadium: PropTypes.bool,
};

const BurgerButtonContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;

  ${props => props.theme.query.lg} {
    width: 100%;
  }
`;

const BurgerButton = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  padding: ${props => `${props.theme.spacing(1)} ${props.theme.spacing(2)}`};
  margin-left: ${props => props.theme.spacing(4)};
  border-bottom: 5px solid transparent;

  &:hover,
  &:focus {
    border-bottom-color: ${props => props.theme.colors.siteHeader.linkActive};
  }

  ${props => props.theme.query.lg} {
    padding: ${props => props.theme.spacing(2)};
  }
`;

const Overlay = styled.div.attrs(props => ({
  'aria-hidden': !props.isOpen,
}))`
  display: ${props => (props.isOpen ? 'block' : 'none')};
  position: fixed;
  top: ${props => props.offset}px;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(38, 50, 56, 0.64);
  z-index: 1;
`;

const Drawer = styled.div.attrs(props => ({
  'aria-hidden': !props.isOpen,
}))`
  position: fixed;
  top: ${props => props.offset}px;
  bottom: 0;
  right: 0;
  width: 300px;
  padding: ${props => props.theme.spacing(4)};
  padding-left: ${props => props.theme.spacing(8)};
  background-color: ${props =>
    props.theme.colors.siteHeader.light.linkBackground};
  z-index: 1;
  overflow: scroll;
`;

const BurgerMenu = ({ headerHeight = 0, isAuthenticated, children, isStadium }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const burgerMenuContentRef = useBodyScrollLock(isOpen);

  useOnClickOutside(burgerMenuContentRef, () => setIsOpen(false));

  useEffect(() => {
    if (!isAuthenticated && window.location.hash === '#user-login') {
      setIsOpen(true);
    }
  }, [isAuthenticated]);

  const onMenuButtonClick = () => {
    if (!isOpen && window.location.hash) {
      window.history.pushState('', document.title, window.location.pathname);
    }
    setIsOpen(!isOpen);
  };

  return (
    <BurgerButtonContainer
      headerHeight={headerHeight}
      ref={burgerMenuContentRef}
      {...preventPropagationProps()}
    >
      <BurgerButton
        isOpen={isOpen}
        onClick={onMenuButtonClick}
        data-hook={
          isOpen
            ? HEADER_BURGER_MENU_COLLAPSE_BUTTON.hook
            : HEADER_BURGER_MENU_BUTTON.hook
        }
        aria-label="Toggle menu"
        aria-controls="burger-menu"
      >
        {isOpen ? (
          <img
            src={isStadium ? BURGER_CLOSE_ICON_WHITE : BURGER_CLOSE_ICON_BLUE}
            alt=""
          />
        ) : (
          <img src={isStadium ? BURGER_ICON_WHITE : BURGER_ICON_BLUE} alt="" />
        )}
      </BurgerButton>
      <Overlay isOpen={isOpen} offset={headerHeight}>
        <Drawer id="burger-menu" isOpen={isOpen} offset={headerHeight}>
          {isOpen && children}
        </Drawer>
      </Overlay>
    </BurgerButtonContainer>
  );
};

BurgerMenu.propTypes = propTypes;

export default BurgerMenu;
